import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";
import { Dot, Loader2, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";

import {
  ConversationMessage,
  useChatMessageQuery,
} from "@/queries/chat/user-chat";
import { useChatSocket } from "@/queries/chat/user-chat-socket";
import { useProfileQuery } from "@/queries/user/profile";
import { useSendChatMessage } from "@/mutations/chat/send-user-chat-message";
import { useStartChatConversation } from "@/mutations/chat/start-user-chat-message";
import { useMarkConversationRead } from "@/mutations/chat/mark-conversation-read";

import { ConversationSummary } from "@/queries/chat/user-conversation";

interface Props {
  conversation?: ConversationSummary;
  participants?: {
    userId: string;
    companyId: string;
  };
  jobOpeningId?: string;
  title: string;
  trigger?: React.ReactNode;
  disabled?: boolean;
}

export interface ChatWindowDialogHandle {
  open: () => void;
}

const ChatWindow = forwardRef<ChatWindowDialogHandle, Props>(
  (
    {
      conversation,
      participants,
      jobOpeningId,
      title,
      trigger,
      disabled,
    }: Props,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [message, setMessage] = useState("");
    const [pendingMessage, setPendingMessage] =
      useState<ConversationMessage | null>(null);

    useImperativeHandle(ref, () => ({
      open: () => setIsOpen(true),
    }));

    const outerRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const prevTopMessageIdRef = useRef<string | null>(null);

    const prevInnerDivHeight = useRef<number>(0);

    const { profile } = useProfileQuery();
    const {
      chatMessage,
      getMoreMessages,
      hasMoreMessages,
      gettingMoreMessages,
    } = useChatMessageQuery(conversation?.id ?? "");
    const { sendMessage, isSendingMessage } = useSendChatMessage(
      conversation?.id ?? "",
    );
    const { startConversation, isStartingConversation } =
      useStartChatConversation(
        participants?.userId ?? "",
        participants?.companyId ?? "",
        jobOpeningId,
      );
    const { markConversationRead, isMarkingConversationRead } =
      useMarkConversationRead(conversation?.id ?? "");

    useChatSocket(conversation?.id ?? "");

    const orderedMessages = useMemo(
      () => chatMessage?.pages.flat().reverse() ?? [],
      [chatMessage],
    );

    useEffect(() => {
      if (!isOpen) {
        // Reset when chat is closed
        setMessage("");
        prevInnerDivHeight.current = 0;
      } else {
        setShowScrollButton(false);
      }
    }, [isOpen, orderedMessages]);

    // Handle scrolling to bottom for new messages
    // and preserving position when older messages are loaded
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      // Use requestAnimationFrame to delay until render
      // That way the refs are actually attached if the dialog just opened
      const frame = requestAnimationFrame(() => {
        if (!outerRef.current || !innerRef.current) {
          return;
        }

        // Scroll position and container height
        const outerDivHeight = outerRef.current.clientHeight;
        const innerDivHeight = innerRef.current.clientHeight;
        const outerDivScrollTop = outerRef.current.scrollTop;

        // Id of oldest message
        const prevTopMessageId = prevTopMessageIdRef.current;
        const newTopMessageId = orderedMessages[0]?.id ?? null;

        // If we're rendering for the first time or we were already scrolled to the bottom
        if (
          !prevInnerDivHeight.current ||
          outerDivScrollTop === prevInnerDivHeight.current - outerDivHeight
        ) {
          outerRef.current.scrollTo({
            top: innerDivHeight - outerDivHeight,
            left: 0,
            behavior: prevInnerDivHeight.current ? "smooth" : "auto",
          });
          // If we weren't scrolled to the bottom
          // and the new message isn't an old message that was loaded in
        } else if (prevTopMessageId && newTopMessageId === prevTopMessageId) {
          setShowScrollButton(true);
        }

        // Update container height and oldest message
        prevInnerDivHeight.current = innerDivHeight;
        prevTopMessageIdRef.current = newTopMessageId;
      });

      return () => {
        cancelAnimationFrame(frame);
      };
    }, [isOpen, orderedMessages]);

    // Clear new message label if user manually scrolls
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      let cleanup: (() => void) | null = null;
      const frame = requestAnimationFrame(() => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry?.isIntersecting) {
              setShowScrollButton(false);
              markRead();
            }
          },
          {
            root: outerRef.current,
            threshold: 1.0,
          },
        );

        const currentEnd = bottomRef.current;
        if (currentEnd) {
          observer.observe(currentEnd);
          cleanup = () => observer.unobserve(currentEnd);
        }
      });

      return () => {
        cancelAnimationFrame(frame);
        cleanup?.();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Handle the cases where we aren't yet scrolling and can see the bottom
    // of the chat log
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const frame = requestAnimationFrame(() => {
        const bottomEl = bottomRef.current;
        const outerEl = outerRef.current;

        if (!bottomEl || !outerEl) {
          return;
        }

        const rootRect = outerEl.getBoundingClientRect();
        const endRect = bottomEl.getBoundingClientRect();

        const isAtBottom = endRect.bottom <= rootRect.bottom;

        if (isAtBottom) {
          setShowScrollButton(false);
          markRead();
        }
      });

      return () => cancelAnimationFrame(frame);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orderedMessages]);

    // Request more messages when we scroll top
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      let cleanup: (() => void) | null = null;
      const frame = requestAnimationFrame(() => {
        const container = outerRef.current;
        if (!container) {
          return;
        }

        const handleScroll = () => {
          if (
            container.scrollTop < 100 &&
            hasMoreMessages &&
            !gettingMoreMessages
          ) {
            void getMoreMessages();
          }
        };

        container.addEventListener("scroll", handleScroll);
        cleanup = () => container.removeEventListener("scroll", handleScroll);
      });

      return () => {
        cancelAnimationFrame(frame);
        cleanup?.();
      };
    }, [isOpen, hasMoreMessages, gettingMoreMessages, getMoreMessages]);

    const handleScrollButtonClick = useCallback(() => {
      if (!bottomRef.current) {
        return;
      }

      bottomRef.current.scrollIntoView({
        behavior: "smooth",
      });

      setShowScrollButton(false);
    }, []);

    const onSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim() || isSendingMessage || isStartingConversation) {
        return;
      }

      if (conversation) {
        sendMessage(message);
      } else {
        // When starting a conversation, make note of the message so it can be optimistically displayed
        const now = new Date().toISOString();
        setPendingMessage({
          id: `temp-${now}`,
          message,
          senderId: profile?.id,
          senderName: profile?.name,
          avatarLink: profile?.profilePictureLink,
          createdAt: now,
          updatedAt: now,
        });
        startConversation(message, {
          onSettled: () => setPendingMessage(null),
        });
      }

      // Scroll down to new message if necessary
      outerRef.current?.scrollTo({
        top: outerRef.current.scrollHeight,
        left: 0,
        behavior: "auto",
      });

      setMessage("");
    };

    const markRead = () => {
      const mostRecentMessage = orderedMessages.at(-1);

      // If we're somehow missing the conversation, message, or are already sending a read receipt
      if (!conversation || isMarkingConversationRead || !mostRecentMessage) {
        return;
      }

      // If the most recent message is a optimistic rather than final one
      if (mostRecentMessage.isOptimistic) {
        return;
      }

      // Don't send a read receipt if we've already sent one more
      // recently than the latest message (i.e. we've already seen it)
      if (
        conversation.lastSeenAt &&
        conversation.lastSeenAt > mostRecentMessage.createdAt
      ) {
        return;
      }

      markConversationRead(mostRecentMessage.id);
    };

    // Improper params
    if (!conversation && !participants) {
      return;
    }

    // Hide the chat for closed opportunities, unless the chat had already been started
    if (!conversation && disabled) {
      return;
    }

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          {trigger ?? (
            <Tooltip content="Send a message">
              <Button variant="outline" className="relative">
                Chat
                {conversation?.lastMessage &&
                  (!conversation.lastSeenAt ||
                    conversation.lastSeenAt <
                      conversation.lastMessage.createdAt) && (
                    <Dot className="absolute !h-6 !w-6 top-0 right-0 !stroke-red-600" />
                  )}
              </Button>
            </Tooltip>
          )}
        </DialogTrigger>
        <DialogContent className="w-full sm:max-w-2xl p-0">
          <DialogHeader className="p-6">
            <div className="flex items-center gap-2">
              <DialogTitle>{title}</DialogTitle>
            </div>
            <DialogDescription className="sr-only">
              Connect and send a message
            </DialogDescription>
          </DialogHeader>
          <ScrollArea
            className="max-h-[60vh] min-h-[60vh] md:min-h-[40vh] px-2"
            viewportRef={outerRef}
          >
            <div className="max-h-full flex flex-col gap-2" ref={innerRef}>
              {gettingMoreMessages && (
                <Loader2 className="m-auto animate-spin" />
              )}
              {pendingMessage && (
                <Message message={pendingMessage} userId={profile?.id} />
              )}
              {orderedMessages.map((message) => (
                <Message
                  key={`chat-${message.id}`}
                  message={message}
                  userId={profile?.id}
                />
              ))}
              <div ref={bottomRef} />
            </div>
          </ScrollArea>
          <form
            onSubmit={onSubmit}
            className="relative flex items-center gap-2 pt-4 p-2 border-t mt-4"
          >
            <Button
              variant="link"
              className={cn(
                "p-1 h-[30px] absolute top-[-38px] left-[50%] translate-x-[-50%] bg-red-600 text-white",
                showScrollButton || "hidden",
              )}
              type="button"
              onClick={handleScrollButtonClick}
            >
              New message!
            </Button>
            <Input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1"
              disabled={isSendingMessage || isStartingConversation}
            />
            <Button
              type="submit"
              disabled={
                !message.trim() || isSendingMessage || isStartingConversation
              }
            >
              Send
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    );
  },
);
ChatWindow.displayName = "ChatWindow";

const Message = ({
  message,
  userId,
}: {
  message: ConversationMessage;
  userId?: string;
}) => {
  const isSender = useMemo(
    () => message.senderId === userId,
    [message.senderId, userId],
  );

  return (
    <div
      className={cn(
        "flex gap-2 items-start my-2",
        isSender ? "justify-end" : "justify-start",
      )}
    >
      {!isSender && <MessageAvatar link={message.avatarLink} />}
      <div
        className={cn(
          "max-w-[70%] rounded-lg p-3",
          isSender ? "bg-blue-600 text-white" : "bg-muted text-foreground",
        )}
      >
        <div className="text-xs">
          {message.senderName} • {new Date(message.createdAt).toLocaleString()}
        </div>
        <div className="text-sm whitespace-pre-line">
          {linkifyText(message.message)}
        </div>
      </div>
      {isSender && <MessageAvatar link={message.avatarLink} />}
    </div>
  );
};

const MessageAvatar = ({ link }: { link?: string }) => {
  return (
    <Avatar className="size-8 min-w-8">
      <AvatarImage
        src={link ?? undefined}
        className="object-cover object-center"
      />
      <AvatarFallback>
        <User className="size-6 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
};

function linkifyText(text: string): React.ReactNode[] {
  // Look for text starting with http(s):// or www. that contain something that could be a TLD
  // Or strings without http(s):// or www. but that have a plausible TLD and url paths
  const urlRegex =
    /\b((?:https?:\/\/|www\.)[^\s]+\.[a-z]{2,63}\S*|\b\w+\.[a-z]{2,63}\/[^\s]*)\b/gi;
  const parts = text.split(urlRegex);

  return parts.map((part, idx) => {
    const trimmedText = part.trim();

    if (urlRegex.test(trimmedText)) {
      const href = trimmedText.startsWith("http")
        ? trimmedText
        : `https://${trimmedText}`;

      return (
        <Fragment key={`message-${idx}`}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline break-words"
          >
            {trimmedText}
          </a>
        </Fragment>
      );
    }

    return <Fragment key={`message-${idx}`}>{part}</Fragment>;
  });
}

export default ChatWindow;
